/**
 * Dialog Card Component
 *
 * Extracted from Figma Crop Circle System
 * Node ID: 523-1146
 *
 * A card component with title, description text, and an optional action button.
 * Supports light and dark modes.
 */

import React from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { Colors } from '../../../../themes/colors';
import { Icon, type IconName } from '../../icons';
import {
  colors,
  typography,
  spacing,
  componentBorderRadius,
  componentShadows,
} from '../../tokens';

export type DialogCardMode = 'light' | 'dark';

export interface DialogCardProps {
  /**
   * Card title text
   * @default "Dialog"
   */
  title?: string;

  /**
   * Card description text
   */
  description?: string;

  /**
   * Button label text
   */
  buttonText?: string;

  /**
   * Callback when the button is pressed
   */
  onButtonPress?: () => void;

  /**
   * Left icon name shown before the title
   */
  leftIcon?: IconName;

  /**
   * Right icon name shown at the top-right
   */
  rightIcon?: IconName;

  /**
   * Display mode
   * @default "light"
   */
  mode?: DialogCardMode;

  /**
   * Test ID for testing
   */
  testID?: string;
}

export const DialogCard: React.FC<DialogCardProps> = ({
  title = 'Dialog',
  description = 'Gripp gives growing operations a single platform to connect teams, track assets, and bring structure to the knowledge and routines that live in people\'s heads.',
  buttonText = 'Button',
  onButtonPress,
  leftIcon = 'Add',
  rightIcon = 'Add',
  mode = 'light',
  testID,
}) => {
  const isDark = mode === 'dark';

  // `mode="dark"` is an authored Figma variant for a permanently dark card, NOT
  // the app's dark theme — same convention as NavigationButton. So its palette
  // stays a set of static hexes while the default variant reads role tokens and
  // flips. Under the app's dark theme the default variant is already dark; this
  // one stays dark in both, which is the point of asking for it.
  const backgroundColor = isDark ? colors.neutral[800] : Colors.role.surface;
  const textColor = isDark ? colors.brand.white : Colors.role.textStrong;
  const descriptionColor = isDark ? colors.slate[300] : Colors.role.textSoft;
  const buttonBg = isDark ? colors.brand.white : Colors.role.surfaceInverse;
  const buttonTextColor = isDark
    ? colors.neutral[800]
    : Colors.role.textOnInverse;
  const iconColor = isDark ? colors.brand.white : Colors.role.textStrong;

  return (
    <View
      style={[styles.container, { backgroundColor }]}
      testID={testID}
      accessibilityRole="none"
    >
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          {leftIcon && (
            <Icon name={leftIcon} size={16} color={iconColor} />
          )}
          <Text style={[styles.title, { color: textColor }]}>{title}</Text>
        </View>
        {rightIcon && (
          <Icon name={rightIcon} size={16} color={iconColor} />
        )}
      </View>

      {/* Description */}
      {description && (
        <Text style={[styles.description, { color: descriptionColor }]}>
          {description}
        </Text>
      )}

      {/* Button */}
      {buttonText && onButtonPress && (
        <Pressable
          style={[styles.button, { backgroundColor: buttonBg }]}
          onPress={onButtonPress}
          accessibilityRole="button"
        >
          <Text style={[styles.buttonText, { color: buttonTextColor }]}>
            {buttonText}
          </Text>
        </Pressable>
      )}
    </View>
  );
};

DialogCard.displayName = 'DialogCard';

const styles = StyleSheet.create({
  container: {
    minWidth: 320,
    maxWidth: 400,
    borderRadius: componentBorderRadius.card.default,
    padding: spacing.lg,
    gap: spacing.md,
    ...componentShadows.card.default,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flex: 1,
  },
  title: {
    ...typography.title.smallEmphasised,
    color: Colors.role.textStrong,
  },
  description: {
    ...typography.body.small,
    color: Colors.role.textStrong,
  },
  button: {
    alignSelf: 'flex-start',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderRadius: componentBorderRadius.button.small,
  },
  buttonText: {
    ...typography.body.smallEmphasised,
    color: Colors.role.textStrong,
  },
});
